import {
  BankIcon,
  CashSpecialIcon,
  Title,
  WalletIcon,
  AddCardIcon,
} from "lola-framework-ui-test";

import { useCallback, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { getBrandInfo } from "../constants/card-brands";
import type {
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
} from "../interfaces/payment-method";
import { type FundingMethod } from "../utils/catalog";
import { formatBankAccountLabel } from "../utils/bankAccountLabel";
import type { FundingMethodDetails } from "../utils/fundingLimits";
import { formatFundingLimitSubtitle } from "../utils/fundingLimits";
import {
  cardFundingKind,
  isCardFundingAvailable,
  unavailableCardReason,
  type UnavailableCardReason,
} from "../utils/card-funding-availability";
import { fundingCardDisplayName } from "../utils/resolve-funding-card";
import { UnavailableFundingCardSheet } from "./UnavailableFundingCardSheet";
import { amountFormatter } from "../utils";
import { PayoutInfo } from "./PayoutInfo";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { ScrollFadeIndicator } from "./ScrollFadeIndicator";
import { CloseLight } from "../assets/navIcons";

const UNAVAILABLE_ROW_KEY: Record<UnavailableCardReason, string> = {
  creditOnDebitRoute: "payment.unavailableCreditShort",
  debitOnCreditRoute: "payment.unavailableDebitShort",
  unclassified: "payment.unavailableUnclassifiedShort",
  generic: "payment.unavailable",
  railRejected: "payment.unavailableRailRejected",
  railTemporarilyUnavailable: "payment.unavailableRailTemporary",
};

const ACCOUNT_METHODS = new Set<FundingMethod>(["ACH", "RTP", "BANK_BALANCE"]);
const PICKER_SKELETON_ROWS = 3;

const PickerListSkeleton = () => (
  <>
    {Array.from({ length: PICKER_SKELETON_ROWS }, (_, i) => (
      <div key={i} className="payout-skeleton" aria-hidden="true">
        <span className="payout-skeleton-icon" />
        <span className="payout-skeleton-lines">
          <span className="payout-skeleton-line" />
          <span className="payout-skeleton-line short" />
        </span>
      </div>
    ))}
  </>
);

export type FlatFundingPick =
  | { kind: "cash" }
  | { kind: "method"; method: FundingMethod }
  | { kind: "card"; card: ICardPaymentMethod }
  | { kind: "account"; account: IBankAccountPaymentMethod }
  | { kind: "add"; method: "DEBIT_CARD" | "CREDIT_CARD" };

export interface SelectFundingMethodSlotProps {
  selectedMethod?: FundingMethod | string | null;
  selectedPaymentMethodId?: string | null;
  available: FundingMethod[];
  cards: ICardPaymentMethod[];
  bankAccounts?: IBankAccountPaymentMethod[];
  /** Instruments or pre-quote still unresolved — full-list shimmer, no grey cards. */
  loading?: boolean;
  details?: Partial<Record<FundingMethod, FundingMethodDetails>>;
  onPick: (pick: FlatFundingPick) => void;
  onClose: () => void;
  /** Cards the rail will not prepare again this session, with the copy reason. */
  blockedRailCards?: ReadonlyMap<string, UnavailableCardReason>;
}

const formatFee = (fee: number | undefined | null): string | null =>
  fee != null ? `$${fee.toFixed(2)} USD` : null;

const formatLimit = (value: number): string =>
  `${amountFormatter(value, "USD", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  })} USD`;

export const SelectFundingMethodSlot: React.FC<SelectFundingMethodSlotProps> = ({
  selectedMethod,
  selectedPaymentMethodId = null,
  available,
  cards,
  bankAccounts = [],
  loading = false,
  details = {},
  onPick,
  onClose,
  blockedRailCards,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;
  const [blockedCard, setBlockedCard] = useState<ICardPaymentMethod | null>(
    null
  );

  const availableSet = new Set(available);
  const debitOk = availableSet.has("DEBIT_CARD");
  const creditOk = availableSet.has("CREDIT_CARD");
  const cashOk = availableSet.has("CASH_DEPOSIT");
  const accountMethods = available.filter((m) => ACCOUNT_METHODS.has(m));
  const accountOk = accountMethods.length > 0;

  const railBlocked = (paymentMethodId: string): boolean =>
    blockedRailCards?.has(paymentMethodId) ?? false;
  const debitCards = cards.filter(
    (c) =>
      cardFundingKind(c) === "DEBIT_CARD" &&
      isCardFundingAvailable(c, available) &&
      !railBlocked(c.paymentMethodId)
  );
  const creditCards = cards.filter(
    (c) =>
      cardFundingKind(c) === "CREDIT_CARD" &&
      isCardFundingAvailable(c, available) &&
      !railBlocked(c.paymentMethodId)
  );
  const unavailableCards = cards.filter(
    (c) =>
      !isCardFundingAvailable(c, available) || railBlocked(c.paymentMethodId)
  );
  const accounts = accountOk ? bankAccounts : [];

  const subtitleFor = (method: FundingMethod): string => {
    const row = details[method];
    return formatFundingLimitSubtitle(
      t,
      row?.minAmount,
      row?.maxAmount,
      formatLimit
    );
  };

  const feeFor = (method: FundingMethod): JSX.Element | undefined => {
    const formattedFee = formatFee(details[method]?.fee);
    return formattedFee ? (
      <label className="funding-fee">
        {t("payment.feeEstimated", { amount: formattedFee })}
      </label>
    ) : undefined;
  };

  const pickIcon = useCallback((brand: string) => {
    let finalIcon: string | null = null;
    if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
    if (brand.toLocaleUpperCase().includes("MASTERCARD"))
      finalIcon = masterCardLogo;
    if (!finalIcon) finalIcon = getBrandInfo(brand.toLowerCase())?.logo ?? null;
    return finalIcon ? (
      <img src={finalIcon} alt={`${brand} logo`} />
    ) : (
      <span />
    );
  }, []);

  const hasRows =
    debitCards.length +
      creditCards.length +
      unavailableCards.length +
      accounts.length +
      (cashOk ? 1 : 0) +
      (debitOk ? 1 : 0) +
      (creditOk ? 1 : 0) +
      (accountOk && accounts.length === 0 ? accountMethods.length : 0) >
    0;

  if (blockedCard) {
    return (
      <UnavailableFundingCardSheet
        card={blockedCard}
        available={available}
        bodyCopy={bodyCopy}
        backgroundBtn={backgroundBtn}
        textColorBtn={textColorBtn}
        reason={blockedRailCards?.get(blockedCard.paymentMethodId)}
        onDismiss={() => setBlockedCard(null)}
      />
    );
  }

  return (
    <section className="select-payer-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <Title title={t("payment.selectMethod")} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label={t("common.cancel")}
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>
      <section
        className="select-payer-list select-funding-list"
        aria-busy={loading}
        style={
          {
            "--bgIcon": backgroundBtn,
            "--iconOnBrand": textColorBtn,
          } as React.CSSProperties
        }
      >
        <ScrollFadeIndicator color={bodyCopy} />
        {loading ? <PickerListSkeleton /> : null}
        {!loading && debitCards.map((card) => {
          const isSelected = selectedPaymentMethodId === card.paymentMethodId;
          return (
            <PayoutInfo
              key={card.paymentMethodId}
              title={fundingCardDisplayName(card)}
              subtitle={subtitleFor("DEBIT_CARD")}
              icon={pickIcon(card.brand)}
              fee={feeFor("DEBIT_CARD")}
              className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "card", card })}
            />
          );
        })}
        {!loading && creditCards.map((card) => {
          const isSelected = selectedPaymentMethodId === card.paymentMethodId;
          return (
            <PayoutInfo
              key={card.paymentMethodId}
              title={fundingCardDisplayName(card)}
              subtitle={subtitleFor("CREDIT_CARD")}
              icon={pickIcon(card.brand)}
              fee={feeFor("CREDIT_CARD")}
              className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "card", card })}
            />
          );
        })}
        {!loading && unavailableCards.map((card) => (
          <PayoutInfo
            key={card.paymentMethodId}
            title={fundingCardDisplayName(card)}
            subtitle={t(UNAVAILABLE_ROW_KEY[unavailableCardReason(card, available)])}
            icon={pickIcon(card.brand)}
            color={bodyCopy}
            unavailable
            className="funding-neutral-icon"
            onChange={() => setBlockedCard(card)}
          />
        ))}
        {!loading && accounts.map((account) => {
          const isSelected =
            selectedPaymentMethodId === account.paymentMethodId;
          return (
            <PayoutInfo
              key={account.paymentMethodId}
              title={
                formatBankAccountLabel(
                  account.bankName,
                  account.maskedAccountNumber,
                ) ?? account.bankName
              }
              subtitle={subtitleFor(accountMethods[0] ?? "ACH")}
              icon={<BankIcon size={22} colors={[bodyCopy, bodyCopy]} />}
              fee={feeFor(accountMethods[0] ?? "ACH")}
              className={`${isSelected ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "account", account })}
            />
          );
        })}
        {!loading &&
          accountOk &&
          accounts.length === 0 &&
          accountMethods.map((method) => (
            <PayoutInfo
              key={method}
              title={t(`funding.methods.${method}`, { defaultValue: method })}
              subtitle={subtitleFor(method)}
              icon={<BankIcon size={22} colors={[bodyCopy, bodyCopy]} />}
              fee={feeFor(method)}
              className={`${selectedMethod === method ? "selected" : ""} funding-neutral-icon`.trim()}
              onChange={() => onPick({ kind: "method", method })}
            />
          ))}
        {!loading && cashOk && (
          <PayoutInfo
            title={t("payment.cashDeposit")}
            subtitle={subtitleFor("CASH_DEPOSIT")}
            icon={<CashSpecialIcon size={22} colors={[bodyCopy, bodyCopy]} />}
            fee={feeFor("CASH_DEPOSIT")}
            className={`${
              selectedMethod === "CASH_DEPOSIT" ? "selected" : ""
            } funding-neutral-icon`.trim()}
            onChange={() => onPick({ kind: "cash" })}
          />
        )}
        {!loading && debitOk && (
          <PayoutInfo
            title={t("payment.addDebitCard")}
            subtitle={subtitleFor("DEBIT_CARD")}
            className="funding-brand-icon"
            icon={
              <AddCardIcon size={22} colors={[textColorBtn, textColorBtn]} />
            }
            fee={feeFor("DEBIT_CARD")}
            onChange={() => onPick({ kind: "add", method: "DEBIT_CARD" })}
          />
        )}
        {!loading && creditOk && (
          <PayoutInfo
            title={t("payment.addCreditCard")}
            subtitle={subtitleFor("CREDIT_CARD")}
            className="funding-brand-icon"
            icon={
              <AddCardIcon size={22} colors={[textColorBtn, textColorBtn]} />
            }
            fee={feeFor("CREDIT_CARD")}
            onChange={() => onPick({ kind: "add", method: "CREDIT_CARD" })}
          />
        )}
        {!loading && !hasRows && (
          <PayoutInfo
            title={t("funding.none")}
            subtitle=""
            icon={<WalletIcon size={22} colors={[bodyCopy, bodyCopy]} />}
            locked
          />
        )}
      </section>
    </section>
  );
};
